import { type AssetType, type IssueType, type ProcedureInitiative } from "@overmap-ai/common"
import {
	deleteAssetType,
	deleteIssueType,
	deleteProcedureInitiative,
	persistSliceReducer,
	RESET_PROJECT_STATE,
	RESET_STATE,
} from "@overmap-ai/core"
import { createSlice, type PayloadAction } from "@reduxjs/toolkit"

import type { AnySerializedFilter } from "../../filtering/filter"
import { APP_DATA_DB_NAME } from "../constants"

export interface MapFilterSliceState {
	assetTypeFilters: Record<AssetType["uuid"], AnySerializedFilter[]>
	issueTypeFilters: Record<IssueType["uuid"], AnySerializedFilter[]>
	activeProcedureInitiatives: Record<AssetType["uuid"], ProcedureInitiative["uuid"] | null>
	procedureInitiativeFilters: Record<ProcedureInitiative["uuid"], AnySerializedFilter[]>
}

const initialState: MapFilterSliceState = {
	assetTypeFilters: {},
	issueTypeFilters: {},
	activeProcedureInitiatives: {},
	procedureInitiativeFilters: {},
}

interface SetFiltersPayload {
	id: string
	filters: AnySerializedFilter[]
}

export const mapFilterSlice = createSlice({
	name: "mapFilters",
	initialState,
	extraReducers: (builder) =>
		builder
			.addCase(RESET_STATE, (state) => Object.assign(state, initialState))
			.addCase(RESET_PROJECT_STATE, (state) => Object.assign(state, initialState))
			.addCase(deleteAssetType, (state, action) => {
				delete state.assetTypeFilters[action.payload]
				delete state.activeProcedureInitiatives[action.payload]
			})
			.addCase(deleteIssueType, (state, action) => {
				delete state.issueTypeFilters[action.payload]
			})
			.addCase(deleteProcedureInitiative, (state, action) => {
				delete state.procedureInitiativeFilters[action.payload]

				for (const [assetTypeId, procedureInitiativeId] of Object.entries(state.activeProcedureInitiatives)) {
					if (procedureInitiativeId !== action.payload) continue
					state.activeProcedureInitiatives[assetTypeId] = null
				}
			}),
	reducers: {
		setAllAssetTypeMapFilters: (state, action: PayloadAction<MapFilterSliceState["assetTypeFilters"]>) => {
			state.assetTypeFilters = action.payload
		},
		setAssetTypeMapFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
			const { id, filters } = action.payload
			state.assetTypeFilters[id] = filters
		},
		setAllIssueTypeMapFilters: (state, action: PayloadAction<MapFilterSliceState["issueTypeFilters"]>) => {
			state.issueTypeFilters = action.payload
		},
		setIssueTypeMapFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
			const { id, filters } = action.payload
			state.issueTypeFilters[id] = filters
		},
		setAllProcedureInitiativeMapFilters: (
			state,
			action: PayloadAction<MapFilterSliceState["procedureInitiativeFilters"]>,
		) => {
			state.procedureInitiativeFilters = action.payload
		},
		setProcedureInitiativeMapFilters: (state, action: PayloadAction<SetFiltersPayload>) => {
			const { id, filters } = action.payload
			state.procedureInitiativeFilters[id] = filters
		},
		setAllActiveProcedureInitiatives: (
			state,
			action: PayloadAction<MapFilterSliceState["activeProcedureInitiatives"]>,
		) => {
			state.activeProcedureInitiatives = action.payload
		},
		setActiveProcedureInitiative: (
			state,
			action: PayloadAction<{
				assetTypeId: AssetType["uuid"]
				procedureInitiativeId: ProcedureInitiative["uuid"] | null
			}>,
		) => {
			const { assetTypeId, procedureInitiativeId } = action.payload
			state.activeProcedureInitiatives[assetTypeId] = procedureInitiativeId
		},
	},
})

export const {
	setAssetTypeMapFilters,
	setIssueTypeMapFilters,
	setAllIssueTypeMapFilters,
	setAllAssetTypeMapFilters,
	setAllProcedureInitiativeMapFilters,
	setProcedureInitiativeMapFilters,
	setAllActiveProcedureInitiatives,
	setActiveProcedureInitiative,
} = mapFilterSlice.actions

export const mapFiltersReducer = persistSliceReducer(mapFilterSlice, APP_DATA_DB_NAME)
